import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, MessageSquareText, Loader2 } from "lucide-react";
import { PHONE, PHONE_RAW } from "./shared";
import TermsAcceptance from "./TermsAcceptance";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptQuote() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [terms, setTerms] = useState(null);
  const [error, setError] = useState("");
  const [accept, setAccept] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/leads/${id}/accept`)
      .then((r) => setLead(r.data))
      .catch(() => setError("notfound"));
    axios
      .get(`${API}/terms`)
      .then((r) => setTerms(r.data))
      .catch(() => {});
  }, [id]);

  const canSubmit = terms && accept?.valid;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/leads/${id}/accept`, {
        agreed_terms: accept.agreed_terms,
        private_lines: accept.private_lines,
        private_lines_other: accept.private_lines_other,
        photo_opt_out: accept.photo_opt_out,
      });
      setAccepted(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setLead((l) => ({ ...l, accepted: true }));
      } else {
        setError("submit");
      }
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="accept-quote-page" className="flex min-h-screen items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg">
        <p className="text-center font-display text-3xl font-black uppercase tracking-tight text-bone">
          Friday<span className="text-amber-500">Stumps</span>
        </p>
        <p className="mt-1 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">
          Fridays only · 100% contactless · Columbus, OH
        </p>

        {!lead && !error && (
          <p data-testid="accept-loading" className="mt-16 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your quote…
          </p>
        )}

        {error === "notfound" && (
          <div data-testid="accept-notfound" className="mt-10 border border-stone-800 bg-charcoal p-8 text-center">
            <p className="font-display text-2xl font-black uppercase text-bone">This link flew the coop.</p>
            <p className="mt-3 text-sm text-stone-400">
              It may be old — text {PHONE} and Eric will send a fresh one.
            </p>
          </div>
        )}

        {lead && (
          <div className="mt-10 border border-stone-800 bg-charcoal">
            <div className="border-b border-stone-800 bg-amber-500/5 px-6 py-4">
              <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-amber-500">
                Your FridayStumps quote{lead.first_name ? ` · for ${lead.first_name}` : ""}
              </p>
            </div>
            <div className="p-6 sm:p-8">
              <p data-testid="accept-headline" className="font-display text-2xl font-black uppercase leading-tight text-bone sm:text-3xl">
                The stump{lead.stump_summary ? "s" : ""} at<br />
                <span className="text-amber-500">{lead.address}</span>
              </p>
              {lead.stump_summary && (
                <p data-testid="accept-stumps" className="mt-4 font-mono text-xs uppercase tracking-[0.15em] text-stone-400">
                  ≈ {lead.stump_summary} wide · Grind day: Friday
                </p>
              )}
              <p data-testid="accept-price" className="mt-6 font-display text-5xl font-black tracking-tight text-bone">
                {lead.quoted_price ? (
                  <>
                    ${lead.quoted_price}
                    <span className="ml-2 align-middle font-mono text-[11px] font-normal uppercase tracking-[0.2em] text-stone-500">
                      flat · as agreed
                    </span>
                  </>
                ) : lead.estimate_low ? (
                  <>
                    ${lead.estimate_low}–${lead.estimate_high}
                    <span className="ml-2 align-middle font-mono text-[11px] font-normal uppercase tracking-[0.2em] text-stone-500">
                      site estimate
                    </span>
                  </>
                ) : (
                  <span className="text-3xl">Price as discussed</span>
                )}
              </p>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500">
                No card upfront — payment's due when it's flat ground.
              </p>

              {accepted || lead.accepted ? (
                <div data-testid="accept-confirmed-panel" className="mt-8 border border-green-700/50 bg-green-500/5 p-6">
                  <p className="flex items-center gap-2 font-display text-xl font-black uppercase text-bone">
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                    {lead.accepted && !accepted ? "Already accepted" : "Accepted — locked in."}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-stone-400">
                    Eric's got it from here — he'll confirm your Friday slot by text. Leave the gate unlocked and the area clear; you'll get a photo when it's flat ground.
                  </p>
                  {accepted && (
                    <p data-testid="accept-terms-note" className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-green-400/80">
                      You've accepted the quote and agreed to our terms of service.
                    </p>
                  )}
                  <p data-testid="accept-811-note" className="mt-4 border border-amber-500/40 bg-amber-500/5 px-4 py-3 font-mono text-[11px] leading-relaxed tracking-[0.05em] text-amber-400">
                    Next: Eric calls 811 for you — utility crews mark underground lines within ~3 days, by law, before any grinding. If marking runs past Thursday, your job simply rolls to the following Friday. Nothing for you to do.
                  </p>
                </div>
              ) : (
                <form data-testid="accept-quote-form" onSubmit={submit} className="mt-8 space-y-4">
                  <p className="text-sm leading-relaxed text-stone-400">
                    Happy with it? Accept below — quick terms check and you're on Friday's route. No account, no card, no site visit.
                  </p>

                  <TermsAcceptance terms={terms} onChange={setAccept} />

                  {error === "submit" && (
                    <p data-testid="accept-error" className="border border-red-700/50 bg-red-500/10 px-4 py-3 font-mono text-xs text-red-400">
                      Something glitched — text {PHONE} instead.
                    </p>
                  )}
                  <button data-testid="accept-submit-btn" type="submit" disabled={submitting || !canSubmit} className="min-h-[52px] w-full bg-amber-500 px-6 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#0c0a09] transition-colors duration-150 hover:bg-amber-400 active:bg-amber-600 disabled:opacity-60">
                    {submitting ? "Accepting…" : "Accept quote & agree to terms"}
                  </button>
                </form>
              )}

              <a data-testid="accept-text-link" href={`sms:${PHONE_RAW}`} className="mt-5 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-stone-500 transition-colors duration-150 hover:text-amber-500">
                <MessageSquareText className="h-3.5 w-3.5" /> Rather text? {PHONE}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
